import type { ReactNode } from "react";

interface PageShellProps {
  title: string;
  subtitle?: ReactNode;
  /** Toolbar slot: range pickers, toggles, refresh. */
  actions?: ReactNode;
  /** Full-width strip under the header: error banners, caveats, meta. */
  banner?: ReactNode;
  /** Small right-aligned note under the header row (fetched-at, source badges). */
  meta?: ReactNode;
  /** Accent stripe colour for the page title — Бізнес uses its own. */
  accent?: "accent" | "money" | "business" | "ok";
  children?: ReactNode;
}

const ACCENT_BAR: Record<NonNullable<PageShellProps["accent"]>, string> = {
  accent: "bg-accent",
  money: "bg-money",
  business: "bg-business",
  ok: "bg-ok",
};

/** Standard page frame: heading row + content area. Use it on every route. */
export default function PageShell({
  title,
  subtitle,
  actions,
  banner,
  meta,
  accent = "accent",
  children,
}: PageShellProps) {
  return (
    // Full-width by user decision 2026-08-10: the 85rem cap left a third of a
    // 1600px monitor empty. Wide screens are spent on multi-column grids inside
    // the page, never on stretching one column.
    <div className="w-full px-3 py-3 sm:px-6 sm:py-6 xl:px-10">
      {/* Phone: title first, controls on their own full-width row. Squeezing a
          range picker and two toggles into the right edge of a 390px screen
          makes them unusable. */}
      <header className="mb-3 flex flex-col gap-2 sm:mb-5 sm:flex-row sm:flex-wrap sm:items-end sm:justify-between sm:gap-3">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className={`h-6 w-0.5 shrink-0 sm:h-7 ${ACCENT_BAR[accent]}`} aria-hidden="true" />
          <div className="min-w-0">
            <h1 className="truncate text-title font-semibold tracking-tight sm:text-title-lg">
              {title}
            </h1>
            {subtitle && <p className="mt-0.5 text-xs text-muted sm:text-sm">{subtitle}</p>}
          </div>
        </div>
        <div className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:items-end">
          {actions && <div className="jv-toolbar">{actions}</div>}
          {meta}
        </div>
      </header>
      {banner && <div className="mb-3 space-y-2 sm:mb-4">{banner}</div>}
      {children}
    </div>
  );
}

/**
 * Section marker between blocks of a long page.
 *
 * The whitespace above it carries the break, so the label can be a quiet micro
 * eyebrow instead of a second-tier headline — the same recipe `ChartCard` uses
 * for card titles. The rule that used to run beside it is gone with the border:
 * a page separates its sections by air, not by drawing lines across them.
 */
export function SectionTitle({
  children,
  hint,
  right,
}: {
  children: ReactNode;
  hint?: ReactNode;
  right?: ReactNode;
}) {
  return (
    <div className="mt-6 mb-3 first:mt-0 sm:mt-8">
      {/* `right` (usually a MetaBadge) is a full row of its own on a phone: as a
          flex sibling of a `flex-1` title it took the width and wrapped the
          heading to two lines instead of wrapping itself. */}
      <div className="flex flex-col gap-1 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-3 sm:gap-y-2">
        <h2 className="min-w-0 text-micro font-medium uppercase tracking-wider text-muted sm:flex-1">
          {children}
        </h2>
        {right}
      </div>
      {hint && <p className="mt-1 text-xs text-muted">{hint}</p>}
    </div>
  );
}

/** Refresh button used in page toolbars. */
export function RefreshButton({
  onClick,
  busy = false,
  label = "Оновити",
  progress,
}: {
  onClick: () => void;
  busy?: boolean;
  label?: string;
  /** Multi-step refreshes (the Monobank sweep) count themselves here. */
  progress?: { done: number; total: number };
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy}
      aria-label={label}
      title={label}
      // The shared button recipe rather than a private copy of it: this used to
      // hand-roll `border border-line … hover:border-accent`, which is the
      // bordered pattern v2 retired, and it sits in the same toolbar as buttons
      // that already inherit `jv-btn`.
      className="jv-btn shrink-0"
    >
      <span className={busy ? "inline-block animate-spin" : "inline-block"} aria-hidden="true">
        ↻
      </span>
      {/* The word is the first thing a phone toolbar can afford to lose: the
          glyph carries it, and the label survives as the accessible name. The
          counter is not — a minutes-long sweep with a mute spinner reads as a
          hang, so «2/5» stays visible even where the word does not fit. */}
      <span className="hidden sm:inline">{busy ? "Оновлюю…" : label}</span>
      {busy && progress && (
        <span className="tabular-nums">
          {progress.done}/{progress.total}
        </span>
      )}
    </button>
  );
}
